import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { NAV_GROUPS } from "../../config/navigation";
import { useAuthInfo } from "../../context/AuthInfoContext";
import { useProcessStatus } from "../../context/ProcessStatusContext";
import { useNavigationGuard } from "../../hooks/useUnsavedChangesGuard";
import Icon from "../common/Icon";
import logoMark from "../../assets/logo-mark.png";

// Een klik met modifier (nieuw tabblad/venster) verlaat de pagina niet: niet onderscheppen.
function isPlainClick(e) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

function NavItem({ item, badge, onFollow }) {
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={(e) => onFollow(e, item.path)}
      className={({ isActive }) => `app-nav-link${isActive ? " is-active" : ""}`}
    >
      <span className="app-nav-link__bar" aria-hidden="true" />
      {item.step ? (
        <span className="app-nav-link__step" aria-hidden="true">
          {item.step}
        </span>
      ) : (
        <span className="app-nav-link__icon">
          <Icon name={item.icon || "list"} size={16} />
        </span>
      )}
      <span className="app-nav-link__label">{item.label}</span>
      {badge ? (
        <span className="app-nav-link__badge">
          {badge}
          <span className="sr-only"> te verwerken</span>
        </span>
      ) : null}
    </NavLink>
  );
}

export default function Sidebar({ id, hidden = false, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { pages, waiting } = useAuthInfo();
  const { badges } = useProcessStatus();
  const { requestNavigation } = useNavigationGuard();

  // Wie nog op toegang wacht (issue #23) heeft geen pagina's: geen menu tonen. Zonder deze regel viel de
  // lege `pages` terug op "alles tonen" (de fail-open van vóór auth/me).
  const groups = waiting
    ? []
    : NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((i) => !pages?.length || pages.includes(i.pageKey)),
      })).filter((g) => g.items.length > 0);

  // Bij niet-opgeslagen wijzigingen eerst bevestiging vragen; anders gewoon navigeren.
  function follow(e, path) {
    if (!isPlainClick(e)) return;
    if (path === pathname) {
      onNavigate?.();
      return;
    }
    e.preventDefault();
    requestNavigation(() => {
      navigate(path);
      onNavigate?.();
    });
  }

  return (
    <aside
      id={id}
      className="app-sidebar"
      aria-hidden={hidden || undefined}
      inert={hidden || undefined}
    >
      <Link to="/" className="app-sidebar__brand" onClick={(e) => follow(e, "/")}>
        <span className="app-sidebar__logo">
          <img src={logoMark} alt="" width="34" height="34" />
        </span>
        <span className="app-sidebar__name">Manuco Invest</span>
      </Link>
      <nav className="app-sidebar__nav" aria-label="Hoofdnavigatie">
        {groups.map((g) => (
          <div key={g.title} className="app-nav-group">
            <div className="app-nav-group__title" id={`nav-group-${g.title}`}>
              {g.title}
            </div>
            <ul className="app-nav-group__list" aria-labelledby={`nav-group-${g.title}`}>
              {g.items.map((item) => (
                <li key={item.key}>
                  <NavItem
                    item={item}
                    badge={item.badgeKey ? badges?.[item.badgeKey] || null : null}
                    onFollow={follow}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
